/**
 * Site Access Service
 * 
 * Manages site-based access control:
 * - Sites = Depots/Branches/Regions (configurable terminology)
 * - Locations = Buildings/Areas within a site
 * 
 * Access Logic:
 * - No site assignments = access to ALL sites (admin/head office)
 * - Has site assignments = only see locations/equipment at those sites
 */

import { supabase } from '@shared/supabaseClient';

/**
 * Get all sites
 * @returns {Promise<Array>}
 */
export const getAllSites = async () => {
  const { data, error } = await supabase
    .from('sites')
    .select('*')
    .eq('is_active', true)
    .order('name');

  if (error) {
    console.error('Error fetching sites:', error);
    return [];
  }

  return data || [];
};

/**
 * Get all site IDs assigned to a user
 * @param {string} userId - The user's database ID
 * @returns {Promise<{siteIds: string[], hasRestrictions: boolean}>}
 */
export const getUserSiteAssignments = async (userId) => {
  if (!userId) {
    return { siteIds: [], hasRestrictions: false };
  }

  const { data, error } = await supabase
    .from('user_sites')
    .select('site_id')
    .eq('user_id', userId);

  if (error) {
    // Table may not exist yet — treat as unrestricted
    return { siteIds: [], hasRestrictions: false };
  }

  // If no entries, user has access to ALL sites (no restrictions)
  const hasRestrictions = data.length > 0;
  const siteIds = data.map(row => row.site_id);

  return { siteIds, hasRestrictions };
};

/**
 * Get full site details for a user's assignments
 * @param {string} userId - The user's database ID
 * @returns {Promise<{sites: Array, hasRestrictions: boolean}>}
 */
/**
 * Get user's site assignments with full site details
 * Returns empty array for unrestricted users (no entries in user_sites)
 * @param {string} userId - User UUID
 * @param {string} userRole - User's role (owner/system-admin should have no restrictions)
 * @returns {Promise<{sites: Array, hasRestrictions: boolean}>}
 */
export const getUserSitesWithDetails = async (userId, userRole = null) => {
  if (!userId) {
    return { sites: [], hasRestrictions: false };
  }

  // Owners and system-admins NEVER have restrictions - they can see all sites
  if (userRole === 'owner' || userRole === 'system-admin') {
    return { sites: [], hasRestrictions: false };
  }

  const { data, error } = await supabase
    .from('user_sites')
    .select(`
      site_id,
      sites (
        id,
        name,
        code,
        address,
        is_active
      )
    `)
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching user sites:', error);
    return { sites: [], hasRestrictions: false };
  }

  const hasRestrictions = data.length > 0;
  const sites = data.map(row => row.sites).filter(Boolean);

  return { sites, hasRestrictions };
};

/**
 * Update a user's site assignments (admin only)
 * @param {string} userId - The user to update
 * @param {string[]} siteIds - Array of site IDs to assign
 * @param {string} adminId - Admin making the change
 */
export const updateUserSiteAssignments = async (userId, siteIds, adminId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  // Delete all existing assignments for this user
  const { error: deleteError } = await supabase
    .from('user_sites')
    .delete()
    .eq('user_id', userId);

  if (deleteError) {
    // Table may not exist yet — non-fatal, depot_id on users row is the primary assignment
    if (deleteError.message?.includes('user_sites') || deleteError.code === '42P01') {
      return { success: true, message: 'user_sites table not present — skipped' };
    }
    throw new Error(`Failed to clear existing assignments: ${deleteError.message}`);
  }

  // If no new sites (admin/unrestricted access), we're done
  if (!siteIds || siteIds.length === 0) {
    return { success: true, message: 'User now has access to all sites' };
  }

  // Insert new assignments
  const assignments = siteIds.map(siteId => ({
    user_id: userId,
    site_id: siteId,
    created_by: adminId,
  }));

  const { error: insertError } = await supabase
    .from('user_sites')
    .insert(assignments);

  if (insertError) {
    if (insertError.message?.includes('user_sites') || insertError.code === '42P01') {
      return { success: true, message: 'user_sites table not present — skipped' };
    }
    throw new Error(`Failed to assign sites: ${insertError.message}`);
  }

  return { 
    success: true, 
    message: `User assigned to ${siteIds.length} site(s)` 
  };
};

/**
 * Get location IDs that belong to specific sites
 * @param {string[]} siteIds - Array of site IDs
 * @returns {Promise<string[]>} Array of location IDs
 */
export const getLocationIdsForSites = async (siteIds) => {
  if (!siteIds || siteIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('asset_locations')
    .select('id')
    .in('site_id', siteIds);

  if (error) {
    console.error('Error fetching locations for sites:', error);
    return [];
  }

  return data.map(row => row.id);
};

/**
 * Filter equipment array by site access
 * @param {Array} equipment - Array of equipment objects with locationId
 * @param {string[]} allowedLocationIds - Location IDs within user's allowed sites
 * @param {boolean} hasRestrictions - Whether user has site restrictions
 * @returns {Array} Filtered equipment
 */
export const filterEquipmentBySites = (equipment, allowedLocationIds, hasRestrictions) => {
  // No restrictions = return all equipment
  if (!hasRestrictions || allowedLocationIds.length === 0) {
    return equipment;
  }

  return equipment.filter(item => 
    allowedLocationIds.includes(item.locationId) ||
    allowedLocationIds.includes(item.location_id)
  );
};

/**
 * Get site access summary for display
 * @param {boolean} hasRestrictions 
 * @param {Array} sites 
 * @param {string} siteLabel - Customized label (e.g., "Depot", "Branch")
 * @returns {string}
 */
export const getSiteAccessSummary = (hasRestrictions, sites, siteLabel = 'Site') => {
  if (!hasRestrictions) {
    return `All ${siteLabel}s`;
  }
  
  if (sites.length === 0) {
    return `No ${siteLabel}s Assigned`;
  }
  
  if (sites.length === 1) {
    return sites[0].name;
  }
  
  return `${sites.length} ${siteLabel}s`;
};

/**
 * Create a new site
 * @param {Object} siteData - Site data
 * @returns {Promise<Object>}
 */
export const createSite = async (siteData) => {
  const { data, error } = await supabase
    .from('sites')
    .insert([siteData])
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create site: ${error.message}`);
  }

  return data;
};

/**
 * Update a site
 * @param {string} siteId - Site ID to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>}
 */
export const updateSite = async (siteId, updates) => {
  const { data, error } = await supabase
    .from('sites')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', siteId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update site: ${error.message}`);
  }

  return data;
};

/**
 * Assign a location to a site
 * @param {string} locationId - Location ID
 * @param {string} siteId - Site ID (or null to unassign)
 */
export const assignLocationToSite = async (locationId, siteId) => {
  const { error } = await supabase
    .from('asset_locations')
    .update({ site_id: siteId })
    .eq('id', locationId);

  if (error) {
    throw new Error(`Failed to assign location to site: ${error.message}`);
  }
};

/**
 * Get user IDs that belong to a specific site
 * Returns users who either:
 * 1. Have an entry in user_sites for this site, OR
 * 2. Have NO entries in user_sites (unrestricted access to all sites)
 * @param {string} siteId - The site ID to get users for
 * @returns {Promise<string[]>} - Array of user IDs
 */
export const getUserIdsForSite = async (siteId) => {
  if (!siteId) return [];

  // Get users explicitly assigned to this site
  const { data: assignedUsers, error: assignedError } = await supabase
    .from('user_sites')
    .select('user_id')
    .eq('site_id', siteId);

  if (assignedError) {
    console.error('Error fetching assigned users:', assignedError);
    return [];
  }

  // Get all users who have ANY site assignments (restricted users)
  const { data: restrictedUsers, error: restrictedError } = await supabase
    .from('user_sites')
    .select('user_id');

  if (restrictedError) {
    console.error('Error fetching restricted users:', restrictedError);
    return [];
  }

  const restrictedUserIds = new Set(restrictedUsers.map(r => r.user_id));
  const assignedUserIds = assignedUsers.map(r => r.user_id);

  // Get all users
  const { data: allUsers, error: allError } = await supabase
    .from('users')
    .select('id');

  if (allError) {
    console.error('Error fetching all users:', allError);
    return assignedUserIds;
  }

  // Users for this site = assigned users + unrestricted users (not in any user_sites)
  const unrestrictedUserIds = allUsers
    .filter(u => !restrictedUserIds.has(u.id))
    .map(u => u.id);

  return [...new Set([...assignedUserIds, ...unrestrictedUserIds])];
};
